// Sprint 7U — real, server-backed owner CUSTOMERS panel. Lives in the creator
// App Detail (owner side of the owner/customer boundary), NEVER in the customer
// app. Reads every customer for an app the creator OWNS via
// GET /v1/apps/:appId/customers (creator session + app_customers_owner_select
// RLS). No fabricated rows: the list is exactly what the server returns. Honest
// loading / empty / error / unavailable states. Read-only at launch (an owner
// can see who their customers are; identity is owned by the customer).
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Users, RotateCw, Mail, Clock } from 'lucide-preact';
import { t } from '../lib/i18n';

// The customer shape the gateway returns (camelCased). Every field is
// optional-tolerant so a leaner row never crashes the panel.
interface Customer {
  id: string;
  email?: string | null;
  displayName?: string | null;
  status?: string | null;
  source?: string | null;
  createdAt?: string;
  lastSeenAt?: string;
}

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: Customer[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' }; // 503 — server store not configured

function relTime(iso?: string): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return t('mgmt.time.justNow');
  if (m < 60) return t('mgmt.time.m', { n: m });
  const h = Math.round(m / 60);
  if (h < 24) return t('mgmt.time.h', { n: h });
  const d = Math.round(h / 24);
  if (d < 30) return t('mgmt.time.d', { n: d });
  return new Date(ts).toLocaleDateString();
}

export function ManageCustomers({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' });

  const fetchCustomers = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/customers`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: Customer[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  return (
    <section aria-label={t('mgmt.cu.title')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Users size={15} /> {t('mgmt.cu.title')}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <button type="button" className="pf-btn pf-btn--sm" onClick={fetchCustomers} disabled={load.s === 'loading'}>
          <RotateCw size={13} /> {t('mgmt.refresh')}
        </button>
      </div>

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
          {[0, 1].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 60, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.cu.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.cu.unavailableS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.cu.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t('mgmt.cu.errAuth') : t('mgmt.cu.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchCustomers}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><Users size={22} /></span>
          <p className="pf-empty__title">{t('mgmt.cu.emptyT')}</p>
          <p className="pf-empty__lede">{t('mgmt.cu.emptyS')}</p>
        </div>
      )}

      {load.s === 'ready' && load.items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {load.items.map((cust) => (
            <div key={cust.id} style={cardStyle}>
              <div className="pf-row" style={{ alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{cust.displayName || cust.email || t('mgmt.cu.fallback')}</span>
                  {cust.email && (
                    <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Mail size={12} /> {cust.email}
                    </span>
                  )}
                </div>
                {/* status is a SERVER value: 'active' gets the localized label; any
                    other raw value renders as-is (honest fallback, never remapped) */}
                {cust.status && cust.status !== 'active'
                  ? <span className="pf-badge">{cust.status}</span>
                  : <span className="pf-badge pf-badge--ok">{t('mgmt.cu.active')}</span>}
              </div>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} /> {t('mgmt.cu.lastSeen', { t: relTime(cust.lastSeenAt) })}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const cardStyle: JSX.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6, padding: 14,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)'
};
